import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './components/Sidebar';
import ChatInterface from './components/ChatInterface';
import ProjectMemoryDrawer from './components/ProjectMemoryDrawer';
import EngineerHireModal from './components/EngineerHireModal';
import ConnectorsModal from './components/ConnectorsModal';
import AuthModal from './components/AuthModal';
import { getStoredUser, logout } from './services/authService';
import {
  fetchThreads,
  fetchThreadMessages,
  deleteThread,
  createPart,
  sendChatMessage,
  streamChatMessage,
  fetchMemory,
  addMemory,
  deleteMemory
} from './services/dashnodeApi';

import {
  INITIAL_CONVERSATIONS,
  INITIAL_MEMORY_STATEMENTS,
  REFERENCE_THREADS
} from './data/mockData';

// Helper to accumulate real-time streaming tool calls & tasks
function processStreamEvent(prevTasks = [], eventType, payload = {}) {
  const tasks = [...prevTasks];

  if (eventType === 'tool_call') {
    const toolName = payload.tool || payload.name || 'tool';
    const agentName = payload.agent || 'coordinator';
    const taskId = payload.id || `task-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    tasks.push({
      id: taskId,
      tool: toolName,
      agent: agentName,
      args: payload.args || payload.arguments || {},
      status: 'running',
      result: null,
      error: null,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    });
  } else if (eventType === 'tool_result') {
    const toolName = payload.tool || payload.name;
    let idx = -1;
    if (payload.id) {
      idx = tasks.findIndex((t) => t.id === payload.id);
    }
    if (idx === -1) {
      for (let i = tasks.length - 1; i >= 0; i--) {
        if (tasks[i].status === 'running' && (!toolName || tasks[i].tool === toolName)) {
          idx = i;
          break;
        }
      }
    }
    if (idx !== -1) {
      tasks[idx] = {
        ...tasks[idx],
        status: payload.error ? 'error' : 'completed',
        result: payload.result ?? payload.preview ?? payload.content ?? 'Task completed successfully',
        error: payload.error ? (typeof payload.error === 'string' ? payload.error : JSON.stringify(payload.error)) : null
      };
    } else if (toolName) {
      tasks.push({
        id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        tool: toolName,
        agent: payload.agent || 'specialist',
        args: {},
        status: payload.error ? 'error' : 'completed',
        result: payload.result ?? payload.preview ?? 'Completed',
        error: payload.error || null,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      });
    }
  } else if (eventType === 'feature_corrected') {
    tasks.push({
      id: `task-corr-${Date.now()}`,
      tool: 'apply_feature_correction',
      agent: 'geometry',
      args: payload.correction || {},
      status: 'completed',
      result: payload.correction || 'Feature correction applied',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    });
  } else if (eventType === 'memory_saved') {
    tasks.push({
      id: `task-mem-${Date.now()}`,
      tool: 'record_shop_rule',
      agent: 'coordinator',
      args: { scope: payload.scope, content: payload.content },
      status: 'completed',
      result: `Stored ${payload.scope} rule: ${payload.content}`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    });
  }

  return tasks;
}

// Helper to map synchronous backend events to task list
function mapEventsToTasks(events = []) {
  let tasks = [];
  for (const ev of events) {
    tasks = processStreamEvent(tasks, ev.type, ev);
  }
  return tasks.map((t) => (t.status === 'running' ? { ...t, status: 'completed' } : t));
}

// Helper to parse backend thread messages and group tool calls/results into assistant tasks
function parseThreadMessages(rawMessages = [], chatId = '') {
  const formatted = [];
  let currentAssistant = null;

  for (let i = 0; i < rawMessages.length; i++) {
    const m = rawMessages[i];
    const role = (m.role || '').toLowerCase();

    if (role === 'user') {
      currentAssistant = null;
      formatted.push({
        id: `msg-${chatId}-user-${i}`,
        sender: 'user',
        timestamp: m.creation
          ? new Date(m.creation).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'Recent',
        text: m.content || ''
      });
    } else if (role === 'assistant') {
      const assistantMsg = {
        id: `msg-${chatId}-asst-${i}`,
        sender: 'assistant',
        timestamp: m.creation
          ? new Date(m.creation).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'Recent',
        text: m.content || '',
        tasks: []
      };
      formatted.push(assistantMsg);
      currentAssistant = assistantMsg;
    } else if (role === 'tool_call') {
      const task = {
        id: `task-${chatId}-${i}`,
        tool: m.tool || 'tool',
        agent: m.agent || 'specialist',
        args: m.args || {},
        status: 'completed',
        result: null,
        timestamp: 'Completed'
      };
      if (currentAssistant) {
        currentAssistant.tasks.push(task);
      } else {
        const placeholder = {
          id: `msg-${chatId}-asst-${i}`,
          sender: 'assistant',
          timestamp: 'Recent',
          text: '',
          tasks: [task]
        };
        formatted.push(placeholder);
        currentAssistant = placeholder;
      }
    } else if (role === 'tool_result') {
      if (currentAssistant && currentAssistant.tasks.length > 0) {
        let matched = false;
        for (let t = currentAssistant.tasks.length - 1; t >= 0; t--) {
          if (
            !currentAssistant.tasks[t].result &&
            (!m.tool || currentAssistant.tasks[t].tool === m.tool)
          ) {
            currentAssistant.tasks[t].result = m.content || m.result || 'Task completed';
            matched = true;
            break;
          }
        }
        if (!matched) {
          currentAssistant.tasks.push({
            id: `task-${chatId}-${i}`,
            tool: m.tool || 'tool',
            agent: m.agent || 'specialist',
            args: {},
            status: 'completed',
            result: m.content || m.result || 'Task completed',
            timestamp: 'Completed'
          });
        }
      } else {
        const task = {
          id: `task-${chatId}-${i}`,
          tool: m.tool || 'tool',
          agent: m.agent || 'specialist',
          args: {},
          status: 'completed',
          result: m.content || m.result || 'Task completed',
          timestamp: 'Completed'
        };
        const placeholder = {
          id: `msg-${chatId}-asst-${i}`,
          sender: 'assistant',
          timestamp: 'Recent',
          text: '',
          tasks: [task]
        };
        formatted.push(placeholder);
        currentAssistant = placeholder;
      }
    }
  }

  return formatted;
}

export default function App() {
  // 1. URL Path & Routing Helper
  const getInitialChatId = () => {
    const hash = window.location.hash?.replace('#', '').trim();
    if (hash && (REFERENCE_THREADS[hash] || hash === 'home')) {
      return hash === 'home' ? 'new' : hash;
    }
    const path = window.location.pathname;
    if (path === '/new') return 'new';
    if (path.startsWith('/chat/')) {
      const id = path.replace('/chat/', '').trim();
      if (id) return id;
    }
    return 'new';
  };

  // 2. Application State
  const [conversations, setConversations] = useState(INITIAL_CONVERSATIONS);
  const [activeChatId, setActiveChatId] = useState(getInitialChatId);

  // Authentication State
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);

  // Layout Drawers & Side Panels State (Only active for logged-in users)
  const [isRailOpen, setIsRailOpen] = useState(() => !!getStoredUser() && typeof window !== 'undefined' && window.innerWidth >= 860);
  const [isMemoryOpen, setIsMemoryOpen] = useState(() => !!getStoredUser() && typeof window !== 'undefined' && window.innerWidth >= 1140);

  // Modals
  const [hireModalState, setHireModalState] = useState({ isOpen: false, engineerId: null, mode: 'hire' });
  const [isConnectorsModalOpen, setIsConnectorsModalOpen] = useState(false);

  // Project Memory State (Synchronized with backend Memory DocType)
  const [memoryStatements, setMemoryStatements] = useState(INITIAL_MEMORY_STATEMENTS);
  const [isGenerating, setIsGenerating] = useState(false);

  const userId = currentUser?.email || 'guest-user';
  const loadedChatsRef = useRef(new Set());

  // Responsive breakpoints detection
  useEffect(() => {
    const updateResponsivePanels = () => {
      const width = window.innerWidth;
      if (!currentUser) {
        setIsRailOpen(false);
        setIsMemoryOpen(false);
        return;
      }
      if (width < 860) {
        setIsRailOpen(false);
        setIsMemoryOpen(false);
      } else if (width < 1140) {
        setIsRailOpen(true);
        setIsMemoryOpen(false);
      } else {
        setIsRailOpen(true);
        setIsMemoryOpen(true);
      }
    };

    updateResponsivePanels();
    window.addEventListener('resize', updateResponsivePanels);
    return () => window.removeEventListener('resize', updateResponsivePanels);
  }, [currentUser]);

  // Sync browser URL on popstate (Back/Forward buttons) & hashchange
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash?.replace('#', '').trim();
      if (hash) {
        setActiveChatId(hash === 'home' ? 'new' : hash);
        return;
      }
      const path = window.location.pathname;
      if (path === '/new') {
        setActiveChatId('new');
      } else if (path.startsWith('/chat/')) {
        const id = path.replace('/chat/', '').trim();
        if (id) setActiveChatId(id);
      }
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);

  // Fetch Conversation Threads & Memory from Backend on mount / user change
  useEffect(() => {
    let isMounted = true;

    async function loadBackendData() {
      const threadRes = await fetchThreads(userId);
      if (isMounted && threadRes.success && threadRes.threads.length > 0) {
        const backendThreads = threadRes.threads.map((t) => ({
          id: t.thread_id,
          title: t.title || 'Engineering Task',
          timestamp: t.updated ? new Date(t.updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Recent',
          timeGroup: 'Today',
          fileName: t.part_id ? `${t.part_id}.step` : null,
          fileSize: t.part_id ? 'STEP · CAD' : null,
          isPinned: false,
          isArchived: false,
          messages: []
        }));

        setConversations((prev) => {
          const existingIds = new Set(backendThreads.map((b) => b.id));
          const nonDuplicated = prev.filter((c) => !existingIds.has(c.id));
          return [...backendThreads, ...nonDuplicated];
        });
      }

      const memRes = await fetchMemory(userId);
      if (isMounted && memRes.success) {
        setMemoryStatements(memRes.memoryList);
      }
    }

    loadBackendData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  // Dedicated function to reload live memory from backend
  const reloadMemory = useCallback(async () => {
    const memRes = await fetchMemory(userId);
    if (memRes.success) {
      setMemoryStatements(memRes.memoryList);
    }
  }, [userId]);

  // Active Chat Object Resolution
  const activeChat = activeChatId === 'new'
    ? {
        id: 'new',
        title: 'New Engineering Task',
        fileName: null,
        fileSize: null,
        messages: []
      }
    : conversations.find((c) => c.id === activeChatId) ||
      REFERENCE_THREADS[activeChatId] || {
        id: 'new',
        title: 'New Engineering Task',
        fileName: null,
        fileSize: null,
        messages: []
      };

  // Derive active engineer chain for highlighting in left rail
  const activeChain = activeChat?.chain || activeChat?.messages?.find((m) => m.chain)?.chain || null;

  // Thread Message Loader for real backend threads
  const loadMessagesForChat = useCallback(async (chatId) => {
    if (!chatId || chatId === 'new' || REFERENCE_THREADS[chatId]) return;
    try {
      const res = await fetchThreadMessages(chatId, userId);
      if (res.success && res.messages) {
        const formatted = parseThreadMessages(res.messages, chatId);
        setConversations((prev) =>
          prev.map((c) => (c.id === chatId ? { ...c, messages: formatted } : c))
        );
      }
    } catch (err) {
      console.warn(`Failed to load thread messages for ${chatId}:`, err);
    }
  }, [userId]);

  useEffect(() => {
    if (!activeChatId || activeChatId === 'new' || REFERENCE_THREADS[activeChatId]) return;
    if (loadedChatsRef.current.has(activeChatId)) return;

    loadedChatsRef.current.add(activeChatId);
    loadMessagesForChat(activeChatId);
  }, [activeChatId, loadMessagesForChat]);

  // Navigation Handlers
  const handleSelectChat = useCallback((chatId) => {
    setActiveChatId(chatId);
    window.location.hash = chatId;
    if (window.innerWidth < 860) {
      setIsRailOpen(false);
    }
    loadMessagesForChat(chatId);
  }, [loadMessagesForChat]);

  const handleNewChat = useCallback(() => {
    setActiveChatId('new');
    window.location.hash = 'home';
    if (window.innerWidth < 860) {
      setIsRailOpen(false);
    }
  }, []);

  const handleDeleteChat = useCallback(async (chatId) => {
    loadedChatsRef.current.delete(chatId);
    await deleteThread(chatId, userId);
    setConversations((prev) => prev.filter((c) => c.id !== chatId));

    if (activeChatId === chatId) {
      setActiveChatId('new');
      window.location.hash = 'home';
    }
  }, [activeChatId, userId]);

  const handlePinChat = (chatId) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, isPinned: !c.isPinned } : c))
    );
  };

  const handleRenameChat = (chatId, newTitle) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, title: newTitle } : c))
    );
  };

  const handleArchiveChat = (chatId) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, isArchived: !c.isArchived } : c))
    );
  };

  // Send Message Handler
  const handleSendMessage = async ({ text, attachedFile }) => {
    const userMsg = {
      id: `msg-${Date.now()}`,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      file: attachedFile,
      text
    };

    let targetThreadId = activeChatId;

    if (activeChatId === 'new' || REFERENCE_THREADS[activeChatId]) {
      targetThreadId = `conv-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
      const titleSummary = text.length > 36 ? `${text.slice(0, 36)}...` : text;

      const newConversation = {
        id: targetThreadId,
        title: titleSummary || 'New Engineering Task',
        timestamp: 'Just now',
        timeGroup: 'Today',
        fileName: attachedFile?.name || null,
        fileSize: attachedFile?.size || null,
        isPinned: false,
        isArchived: false,
        messages: [userMsg]
      };

      setConversations((prev) => [newConversation, ...prev]);
      setActiveChatId(targetThreadId);
      window.location.hash = targetThreadId;
    } else {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetThreadId ? { ...c, messages: [...c.messages, userMsg] } : c
        )
      );
    }

    setIsGenerating(true);

    try {
      let resolvedPartId = attachedFile?.partId;

      if (attachedFile?.rawFile) {
        try {
          const uploadRes = await createPart({
            file: attachedFile.rawFile,
            name: attachedFile.name,
            userId
          });

          if (uploadRes.success && uploadRes.part?.id) {
            resolvedPartId = uploadRes.part.id;
            setConversations((prev) =>
              prev.map((c) =>
                c.id === targetThreadId ? { ...c, partId: resolvedPartId } : c
              )
            );
          } else if (uploadRes.requiresAuth) {
            handleRequireAuth({ text, attachedFile });
            return;
          }
        } catch (uploadErr) {
          console.warn('CAD upload exception:', uploadErr);
        }
      }

      if (!resolvedPartId && attachedFile?.name) {
        resolvedPartId = attachedFile.name.replace(/\.[^/.]+$/, '');
      }

      let streamSucceeded = false;
      let streamedReply = '';
      let backendErrorMessage = '';
      const assistantMsgId = `msg-resp-${Date.now()}`;

      const initialAssistantMsg = {
        id: assistantMsgId,
        sender: 'assistant',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        text: '',
        tasks: []
      };

      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetThreadId ? { ...c, messages: [...c.messages, initialAssistantMsg] } : c
        )
      );

      try {
        await streamChatMessage({
          message: text,
          threadId: targetThreadId,
          partId: resolvedPartId || undefined,
          userId,
          onEvent: (eventType, payload) => {
            if (eventType === 'final' && payload.reply) {
              streamedReply = payload.reply;
              streamSucceeded = true;
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === targetThreadId
                    ? {
                        ...c,
                        messages: c.messages.map((m) =>
                          m.id === assistantMsgId
                            ? {
                                ...m,
                                text: streamedReply,
                                tasks: (m.tasks || []).map((t) =>
                                  t.status === 'running' ? { ...t, status: 'completed' } : t
                                )
                              }
                            : m
                        )
                      }
                    : c
                )
              );
            } else if (
              eventType === 'tool_call' ||
              eventType === 'tool_result' ||
              eventType === 'feature_corrected' ||
              eventType === 'memory_saved'
            ) {
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === targetThreadId
                    ? {
                        ...c,
                        messages: c.messages.map((m) =>
                          m.id === assistantMsgId
                            ? {
                                ...m,
                                tasks: processStreamEvent(m.tasks || [], eventType, payload)
                              }
                            : m
                        )
                      }
                    : c
                )
              );
            } else if (eventType === 'error') {
              backendErrorMessage = payload.message || 'Stream error occurred.';
            }
          }
        });
      } catch (streamErr) {
        console.warn('Backend stream error:', streamErr);
        if (!backendErrorMessage) backendErrorMessage = streamErr.message;
        if (streamErr.requiresAuth) {
          handleRequireAuth({ text, attachedFile });
          return;
        }
      }

      if (!streamSucceeded || !streamedReply) {
        const syncRes = await sendChatMessage({
          message: text,
          threadId: targetThreadId,
          partId: resolvedPartId || undefined,
          userId
        });

        if (syncRes.requiresAuth) {
          handleRequireAuth({ text, attachedFile });
          return;
        }

        if (syncRes.success && syncRes.data?.reply) {
          streamedReply = syncRes.data.reply;
          streamSucceeded = true;
          const syncTasks = mapEventsToTasks(syncRes.data.events || []);
          if (syncTasks.length > 0) {
            setConversations((prev) =>
              prev.map((c) =>
                c.id === targetThreadId
                  ? {
                      ...c,
                      messages: c.messages.map((m) =>
                        m.id === assistantMsgId ? { ...m, tasks: syncTasks } : m
                      )
                    }
                  : c
              )
            );
          }
        } else if (syncRes.error) {
          backendErrorMessage = syncRes.error;
        }
      }

      const finalText = streamSucceeded && streamedReply
        ? streamedReply
        : backendErrorMessage
        ? `> ⚠️ **Backend Error**: ${backendErrorMessage}`
        : 'No response received from backend.';

      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetThreadId
            ? {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        text: finalText,
                        tasks: (m.tasks || []).map((t) =>
                          t.status === 'running' ? { ...t, status: 'completed' } : t
                        )
                      }
                    : m
                )
              }
            : c
        )
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Example Task Selection
  const handleSelectExampleTask = (exampleId, queryText) => {
    if (REFERENCE_THREADS[exampleId]) {
      const thread = REFERENCE_THREADS[exampleId];
      setConversations((prev) => {
        if (prev.some((c) => c.id === exampleId)) return prev;
        return [thread, ...prev];
      });
      setActiveChatId(exampleId);
      window.location.hash = exampleId;
    } else {
      handleSendMessage({ text: queryText, attachedFile: null });
    }
  };

  const handleRequireAuth = (promptPayload) => {
    setPendingPrompt(promptPayload);
    setIsAuthModalOpen(true);
  };

  const handleAuthSuccess = (user) => {
    setCurrentUser(user);
    setIsAuthModalOpen(false);

    if (pendingPrompt) {
      pendingPrompt.onSuccess?.();
      handleSendMessage({
        text: pendingPrompt.text,
        attachedFile: pendingPrompt.attachedFile
      });
      setPendingPrompt(null);
    }
  };

  const handleSignOut = () => {
    logout();
    setCurrentUser(null);
  };

  const handleOpenAuth = () => {
    setIsAuthModalOpen(true);
  };

  // Memory CRUD
  const handleAddStatement = async (payload) => {
    let group = 'Company';
    let subtitle = `Rule-${Date.now().toString(36)}`;
    let memory_content = '';

    if (typeof payload === 'string') {
      memory_content = payload;
    } else if (payload && typeof payload === 'object') {
      group = payload.group || payload.Group || 'Company';
      subtitle = payload.subtitle || payload.Subtitle || payload.memory_key || payload.key || `Rule-${Date.now().toString(36)}`;
      memory_content = payload.memory_content || payload.MemoryContent || payload['Memory Content'] || payload.memory_context || payload.content || payload.text || '';
    }

    const tempId = `mem-${Date.now()}`;
    const newEntry = {
      id: tempId,
      key: subtitle,
      group,
      subtitle,
      memory_content,
      // Compatibility aliases
      memory_key: subtitle,
      memory_context: memory_content,
      text: memory_content,
      scope: (group || 'shop').toLowerCase()
    };

    setMemoryStatements((prev) => [newEntry, ...prev]);

    const res = await addMemory({
      group,
      subtitle,
      memory_content,
      content: memory_content,
      memory_key: subtitle,
      memory_context: memory_content,
      scope: (group || 'shop').toLowerCase(),
      userId
    });

    if (res.success && res.data?.key) {
      setMemoryStatements((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, id: res.data.key, key: res.data.key } : m))
      );
    }
  };

  const handleDeleteStatement = async (id, scope = 'shop') => {
    setMemoryStatements((prev) => prev.filter((stmt) => stmt.id !== id && stmt.key !== id));
    await deleteMemory(id, scope, userId);
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#F3F6F9] text-[#1C2838] font-sans relative">
      {/* Mobile Backdrop Scrim when a drawer is open */}
      {currentUser && (isRailOpen || isMemoryOpen) && (
        <div
          onClick={() => {
            if (window.innerWidth < 860) setIsRailOpen(false);
            if (window.innerWidth < 1140) setIsMemoryOpen(false);
          }}
          className="fixed inset-0 bg-[#102442]/40 z-30 lg:hidden block"
        />
      )}

      {/* 1. LEFT RAIL (Sidebar - Only for Logged In Users) */}
      {currentUser && (
        <div
          className={`${
            isRailOpen ? 'translate-x-0' : '-translate-x-full lg:hidden'
          } fixed lg:static top-0 left-0 h-full z-40 transition-transform duration-200 ease-in-out shrink-0`}
        >
          <Sidebar
            conversations={conversations}
            activeChatId={activeChatId}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onDeleteChat={handleDeleteChat}
            onPinChat={handlePinChat}
            onRenameChat={handleRenameChat}
            onArchiveChat={handleArchiveChat}
            currentUser={currentUser}
            onOpenAuth={handleOpenAuth}
            onSignOut={handleSignOut}
            onOpenConnectors={() => setIsConnectorsModalOpen(true)}
            onOpenHireModal={(id, mode) => setHireModalState({ isOpen: true, engineerId: id, mode })}
            onToggleMemory={() => setIsMemoryOpen(!isMemoryOpen)}
            activeChain={activeChain}
          />
        </div>
      )}

      {/* 2. CENTER WORKSPACE (ChatInterface) */}
      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden relative">
        <ChatInterface
          activeChat={activeChat}
          onSendMessage={handleSendMessage}
          isGenerating={isGenerating}
          isMemoryOpen={isMemoryOpen}
          onToggleMemory={() => setIsMemoryOpen(!isMemoryOpen)}
          memoryCount={memoryStatements.length}
          currentUser={currentUser}
          onRequireAuth={handleRequireAuth}
          onOpenAuth={handleOpenAuth}
          onOpenConnectors={() => setIsConnectorsModalOpen(true)}
          onToggleRail={() => setIsRailOpen(!isRailOpen)}
          onBackHome={handleNewChat}
          onSelectExampleTask={handleSelectExampleTask}
        />
      </div>

      {/* 3. RIGHT MEMORY PANEL (ProjectMemoryDrawer - Only when open) */}
      {isMemoryOpen && (
        <div
          className={`${
            isMemoryOpen ? 'translate-x-0' : 'translate-x-full xl:hidden'
          } fixed xl:static top-0 right-0 h-full z-40 transition-transform duration-200 ease-in-out shrink-0`}
        >
          <ProjectMemoryDrawer
            isOpen={isMemoryOpen}
            onClose={() => setIsMemoryOpen(false)}
            memoryStatements={memoryStatements}
            onAddStatement={handleAddStatement}
            onDeleteStatement={handleDeleteStatement}
            onRefresh={reloadMemory}
            activeThreadId={activeChatId}
          />
        </div>
      )}

      {/* MODAL: Engineer Hire / Rent */}
      {currentUser && (
        <EngineerHireModal
          isOpen={hireModalState.isOpen}
          engineerId={hireModalState.engineerId}
          mode={hireModalState.mode}
          onClose={() => setHireModalState({ isOpen: false, engineerId: null, mode: 'hire' })}
          onSuccess={(id, mode) => {
            console.info(`Engineer ${id} ${mode} success`);
          }}
        />
      )}

      {/* MODAL: Connectors */}
      {currentUser && (
        <ConnectorsModal
          isOpen={isConnectorsModalOpen}
          onClose={() => setIsConnectorsModalOpen(false)}
        />
      )}

      {/* MODAL: Authentication */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setPendingPrompt(null);
        }}
        onAuthSuccess={handleAuthSuccess}
        promptContext={pendingPrompt}
      />
    </div>
  );
}
